import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fdate, mxn } from "@/lib/format";
import { TIPOS_OT } from "@/lib/arrendamiento";
import { FilasResumen, MEDIO_INICIAL, MedioPagoCampos, OtDialog, useAccionOt } from "./OtShared";

// Comprar: residual + IVA → Liquidado y carta de transmisión de propiedad.
export function CompraDialog({ open, onOpenChange, ot, creditoId, onDone }) {
  const [medio, setMedio] = useState(MEDIO_INICIAL);
  const { busy, ejecutar } = useAccionOt(creditoId, onDone);
  useEffect(() => { if (open) setMedio(MEDIO_INICIAL); }, [open]);
  const cifras = [["Valor residual", mxn(ot.residual)], ["IVA del residual", mxn(ot.iva_residual)],
    ["Total a pagar", mxn(ot.total_residual), true]];
  return (
    <OtDialog open={open} onOpenChange={onOpenChange} testid="ot-compra-dialog" titulo="Comprar el bien"
      descripcion="El arrendatario paga el valor residual más IVA y adquiere el bien."
      puedeRevisar={!!medio.medio_pago} busy={busy} textoConfirmar={`Registrar pago de ${mxn(ot.total_residual)}`}
      onConfirmar={async () => { if (await ejecutar("compra", medio, "Opción de compra ejercida: crédito liquidado")) onOpenChange(false); }}
      form={<><FilasResumen filas={cifras} testid="ot-compra-cifras" /><MedioPagoCampos valor={medio} onChange={setMedio} prefijo="ot-compra" /></>}
      resumen={<>
        <FilasResumen testid="ot-compra-resumen" filas={[...cifras, ["Medio de pago", medio.medio_pago],
          ["Banco · referencia", `${medio.banco || "—"} · ${medio.referencia || "—"}`], ["Fecha del pago", fdate(new Date().toISOString().slice(0, 10))]]} />
        <p className="text-xs text-slate-600">El pago se aplica solo a la fila del residual; el crédito queda <b>Liquidado</b> y se emite la
          carta de transmisión de propiedad (el CFDI de la enajenación se emite por separado).</p>
      </>} />
  );
}

// Residual vencido: primero se paga SOLO la mora + IVA.
export function MoraDialog({ open, onOpenChange, ot, creditoId, onDone }) {
  const [medio, setMedio] = useState(MEDIO_INICIAL);
  const { busy, ejecutar } = useAccionOt(creditoId, onDone);
  useEffect(() => { if (open) setMedio(MEDIO_INICIAL); }, [open]);
  const cifras = [["Días de atraso", ot.dias_vencidos], ["Mora del residual", mxn(ot.mora)], ["IVA de la mora", mxn(ot.iva_mora)],
    ["Total a pagar", mxn(ot.mora_total), true]];
  return (
    <OtDialog open={open} onOpenChange={onOpenChange} testid="ot-mora-dialog" titulo="Pagar la mora del residual"
      descripcion={`Base: valor residual ${mxn(ot.residual)} desde la fecha límite ${fdate(ot.fecha_limite)}.`}
      puedeRevisar={!!medio.medio_pago} busy={busy} textoConfirmar={`Pagar ${mxn(ot.mora_total)}`}
      onConfirmar={async () => { if (await ejecutar("pagar-mora", { ...medio, monto: ot.mora_total }, "Mora del residual pagada")) onOpenChange(false); }}
      form={<><FilasResumen filas={cifras} testid="ot-mora-cifras" /><MedioPagoCampos valor={medio} onChange={setMedio} prefijo="ot-mora" /></>}
      resumen={<>
        <FilasResumen testid="ot-mora-resumen" filas={[...cifras, ["Medio de pago", medio.medio_pago]]} />
        <p className="text-xs text-slate-600">Se aplica únicamente a la mora y su IVA; el residual sigue pendiente y después puedes ejercer la opción.</p>
      </>} />
  );
}

// Solo el administrador cancela Compra o Devolución, con motivo.
export function CancelarOpcionDialog({ open, onOpenChange, ot, creditoId, onDone }) {
  const [motivo, setMotivo] = useState("");
  const { busy, ejecutar } = useAccionOt(creditoId, onDone);
  useEffect(() => { if (open) setMotivo(""); }, [open]);
  const tipo = TIPOS_OT[ot.opcion?.tipo] || "";
  const efecto = ot.opcion?.tipo === "compra"
    ? "Se cancela el pago del residual, la carta queda marcada CANCELADA y el crédito vuelve a Vigente con el residual pendiente."
    : "El residual vuelve a quedar pendiente, el crédito vuelve a Vigente y el bien sale de «Bienes devueltos».";
  return (
    <OtDialog open={open} onOpenChange={onOpenChange} testid="ot-cancelar-dialog" titulo={`Cancelar ${tipo.toLowerCase()}`}
      descripcion={efecto} destructivo puedeRevisar={motivo.trim().length >= 4} busy={busy} textoConfirmar={`Cancelar ${tipo.toLowerCase()}`}
      onConfirmar={async () => { if (await ejecutar("cancelar", { motivo }, `${tipo} cancelada`)) onOpenChange(false); }}
      form={<>
        <Label className="text-xs">Motivo de la cancelación <span className="text-red-600">*</span></Label>
        <Textarea data-testid="ot-cancelar-motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </>}
      resumen={<FilasResumen testid="ot-cancelar-resumen" filas={[["Opción", tipo], ["Folio", ot.opcion?.folio], ["Ejercida el", fdate(ot.opcion?.fecha)],
        ["Motivo", motivo], ["Efecto", efecto]]} />} />
  );
}
